/**
 * PlethWaveformGenerator — v10.3.0
 *
 * Onda de pletismografía (Pleth) que representa la llegada del pulso al
 * dedo. Consideraciones críticas:
 *
 *   ✔ Frecuencia = pulse_rate (o heart_rate si no está)
 *   ✔ Amplitud = f(perfusion, pleth_amplitude) — NO se usa SpO₂ como
 *     coordenada Y (la SpO₂ es un número, no una posición).
 *   ✔ Baseline SIEMPRE en 0 (sube y baja alrededor pero no se desplaza).
 *   ✔ Aparece ~200ms después del QRS del ECG (Pulse Transit Delay).
 *   ✔ Ritmos sin pulso (FV, asistolia, AESP) → onda plana.
 */
import { PULSE_TRANSIT_DELAY_MS, PULSELESS_RHYTHMS, CHAOTIC_RHYTHMS } from './engine';
import { sampleArtifact } from './artifacts';

// Morfología de un pulso Pleth: subida rápida (systolic upstroke),
// muesca dicrótica (dicrotic notch), descenso más lento (diastolic decay).
function plethPulseShape(phase) {
  // phase ∈ [0, 1)
  if (phase < 0) return 0;
  if (phase < 0.18) {
    // Ascenso sistólico
    return Math.sin((phase / 0.18) * (Math.PI / 2));
  }
  if (phase < 0.32) {
    // Descenso post-sistólico hacia la muesca dicrótica
    return 1 - ((phase - 0.18) / 0.14) * 0.35;
  }
  if (phase < 0.42) {
    // Muesca dicrótica (pequeño rebote)
    return 0.65 + Math.sin((phase - 0.32) / 0.10 * Math.PI) * 0.08;
  }
  if (phase < 1.0) {
    // Diastólico — descenso exponencial suave hacia 0
    const p = (phase - 0.42) / 0.58;
    return 0.65 * Math.exp(-p * 3.2);
  }
  return 0;
}

/**
 * Muestrea la onda Pleth en `timeMs`.
 * @param {number} timeMs
 * @param {object} cfg — salida de resolveSignalConfig()
 */
export function samplePleth(timeMs, cfg) {
  const { rhythm, pulseRate, perfusion, plethAmp, noise } = cfg;

  // Ritmos sin pulso → señal casi plana con sólo ruido de electrodo/sensor.
  if (PULSELESS_RHYTHMS.has(rhythm) || CHAOTIC_RHYTHMS.has(rhythm) || !pulseRate || pulseRate <= 0 || perfusion <= 0) {
    const { offset } = sampleArtifact(timeMs, cfg._vitals || {}, 'pleth');
    return offset * 0.4; // sólo ruido
  }

  // Aplicar Pulse Transit Delay: la onda de pulso llega ~200ms después
  // del QRS que la generó. Restar el retraso del tiempo de muestreo hace
  // que si el ECG dibuja QRS en t=1000, la Pleth dibuja su pico en t=1200.
  const tPulse = timeMs - PULSE_TRANSIT_DELAY_MS;

  const period = 60000 / pulseRate;
  const phase = ((tPulse % period) + period) % period / period;

  // Amplitud escalada por perfusión y amp del sensor. Rango en canvas ~30
  const amplitude = 30 * perfusion * plethAmp * (noise?.signalAttenuation ?? 1);
  const value = plethPulseShape(phase) * amplitude;

  // Añadir artefactos/ruido sobre la señal base (sin mover baseline)
  const { offset } = sampleArtifact(timeMs, cfg._vitals || {}, 'pleth');
  return value + offset;
}
